/**
 * What "published means sold" costs a record, and how the price is written down.
 *
 * PRD 03 §4.4 (amended 2026-09-06): a published medium is one a family can order, so it must
 * carry a displayed `price` and a displayed `shipsIn`. That rule lives here rather than in the
 * admin form, because three surfaces need the same answer — the form decides whether Publish
 * may be pressed, and the store and the Studio decide whether there is a price to show at all.
 *
 * **Missing is a defect, not a placeholder.** A published row without a price is the same kind
 * of fault as one without a `formatLabel`: something to fix in the catalog, never `[ TBD ]` on
 * a page. So nothing here returns a stand-in string — the surfaces render the price or they
 * render no price line, and the admin refuses the publish.
 *
 * `publishBlockers` guards the way in; `attentionReasons` is the same question asked of a row
 * that is already on the shelf, for the rows that got there before the gate existed.
 */
/**
 * What is stopping this record from being published, in the order a form should mention them.
 *
 * Empty means it may be published. Takes a partial because the admin asks about a draft that
 * is still being typed, not about a stored `Medium`.
 */
export function publishBlockers(draft) {
    const blockers = [];
    if (draft.price === undefined)
        blockers.push('price');
    if (draft.shipsIn === undefined || draft.shipsIn.trim() === '')
        blockers.push('shipsIn');
    return blockers;
}
/** Whether this record may be published — the same rule, asked the other way round. */
export function canPublish(draft) {
    return publishBlockers(draft).length === 0;
}
/**
 * Whether a surface should show commerce for this medium at all.
 *
 * Published **and** priced. An unpublished or coming-soon row shows no price, no lead time and
 * no buy control anywhere — and a published row whose price never made it in shows none of it
 * either, because a keepsake page with a lead time and no price is worse than one with
 * neither.
 */
export function isSellable(medium) {
    return medium.published === true && canPublish(medium);
}
/**
 * The price as a person reads it, in their own locale.
 *
 * `Intl` is the runtime's, not a dependency — this package still takes none. The locale is the
 * caller's BCP 47 tag because the store and the Studio each know their own; the currency is
 * the record's, so a Turkish reader sees a euro price in euros, formatted the Turkish way,
 * rather than a converted number nobody charged.
 */
export function formatPrice(price, locale) {
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: price.currency,
        // Whole prices read as "€24", not "€24.00"; anything finer keeps its minor units.
        minimumFractionDigits: Number.isInteger(price.amount) ? 0 : 2,
    }).format(price.amount);
}
/**
 * What is wrong with a row that is already on the shelf, in the order a surface should say it.
 *
 * The publish gate only ever runs on the way *in*: rows published before it existed sit there
 * priceless, and nothing on the catalog list says so. This is that question asked of a stored
 * record rather than of a draft — empty for anything unpublished, because an unpublished row
 * missing a price is a draft, not a defect.
 *
 * `formatLabel` joins the list because its absence is the same class of fault and the store
 * hides it: `formatKey` falls back to the family, so the card gets a plausible wrong chip
 * instead of a visible gap. `validateMedium` says so on the record's own page; this says it
 * where the catalog is scanned.
 */
export function attentionReasons(medium) {
    if (!medium.published)
        return [];
    const reasons = [...publishBlockers(medium)];
    // Exactly `validateMedium`'s rule: a medium that renders has a format to name.
    if (medium.composition !== undefined && (medium.formatLabel?.en ?? '').trim() === '') {
        reasons.push('formatLabel');
    }
    return reasons;
}
//# sourceMappingURL=commerce.js.map